<template>
  <div class='vulnerable-technology-issue-body'>
    <div class="description-block">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container">
        {{description}}
      </div>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <div class="text-container">
        {{asset}}
      </div>
    </div>
    <div class="tld-block">
    <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
    <div class="text-container">
      {{tld}}
    </div>
  </div>
    <div class="technical-block">
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data' v-if='technologyName'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.technology.name')}}</div>
            <div class='text-container technology-name'>{{ technologyName }}</div>
          </div>
          <div class='details-column technical-data' v-if='technologyVersion'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.technology.version')}}</div>
            <div class='text-container technology-title'>{{ technologyVersion }}</div>
          </div>
        </div>
      </div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data' v-if='cveName'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.name')}}</div>
            <div class='text-container cve-name'>{{ cveName }}</div>
          </div>
          <div class='details-column technical-data' v-if='cvssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.cvss')}}</div>
            <div class='text-container cvss-score-section'>
              <cvss-score :value='cvssScore'/>
            </div>
          </div>
          <div class='details-column technical-data' v-if='epssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.epss')}}</div>
            <div class='text-container cve-epss'>
              <epss-score :value='epssFormattedScore'/>
            </div>
          </div>
          <div class='details-column technical-data' v-if='trend'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.trend')}}</div>
            <div class='text-container cve-trend'>{{ trend }}</div>
          </div>
        </div>
        <div class="url-block" v-if='urls.length'>
          <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.url')}}</div>
            <div v-for='(url, index) in urls' :key='index'>
             <div class='url-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
             <div class='text-container'>
               <a :href='url' target="_blank"> {{url}} </a>
             </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="cve-vector-block" v-if='vector && vectorString'>
      <div class="data-title">{{$t('issueDataPopup.cve.vector.data')}}</div>
      <div class="cve-vector-data">
        <div class='text-container'>
          <div class='details-column cve-vector-details'>
            <div class="data-sub-title">{{ $t('cve.vector.details.vectorString') }}</div>
            <div class='text-container'>{{ vectorString }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class='cve-enrichment-apt-groups' v-if="hasEnrichment && aptGroups.length > 0">
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.enrichment.apt.groups')}}</div>
      <div v-for='(group, index) in aptGroups' :key='index'>
        <div class='url-option'>
          <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
          </div>
          <div class='text-container'>
             {{group}}
          </div>
        </div>
      </div>
    </div>
    <div class='cve-enrichment-display-name' v-if="hasEnrichment && displayName.length > 0">
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.enrichment.display.name')}}</div>
      <div class='text-container'>
        {{displayName}}
      </div>
    </div>
    <div class='cve-enrichment-hackers-perspective' v-if="hasEnrichment && hackersPerspective.length > 0">
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.enrichment.hackers.perspective')}}</div>
      <div class='text-container'>
        {{hackersPerspective}}
      </div>
    </div>
    <div class='cve-enrichment-mitigations' v-if="hasEnrichment && mitigations.length > 0">
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.enrichment.mitigations')}}</div>
      <div v-for='(mitigation, index) in mitigations' :key='index'>
        <div class='url-option'>
          <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
          </div>
          <div class='text-container'>
            {{mitigation}}
          </div>
        </div>
      </div>
    </div>
    <div class='cve-enrichment-references' v-if="hasEnrichment && references.length > 0">
      <div class="data-title">{{$t('issueDataPopup.technologies.cve.enrichment.references')}}</div>
      <div v-for='(url, index) in references' :key='index'>
        <div class='url-option'>
          <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
          </div>
          <div class='text-container'>
            <a :href='url' target="_blank"> {{url}} </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EpssScore from '@/components/common/EpssScore.vue';
import { specialTechNames } from '@/constants/technologies';
import CvssScore from '@/components/common/CvssScore.vue';

export default {
  name: 'TechnologyIssueBody',
  components: { CvssScore, EpssScore },
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasEnrichment() {
      return Object.keys(this.issue.metaData?.enrichment || {}).length > 0;
    },
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset;
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.metaData?.description || this.issue.description;
    },
    technologyName() {
      return Object.keys(specialTechNames)
        .includes(this.metaData.technologyName.toLowerCase())
        ? specialTechNames[this.metaData.technologyName.toLowerCase()]
        : this.metaData.technologyName;
    },
    technologyVersion() {
      return this.metaData.technologyVersion;
    },
    cveName() {
      return this.metaData.cveName;
    },
    cvss() {
      return this.metaData.cvss;
    },
    cvssScore() {
      const value = Object.keys(this.cvss)[0] !== 'None' ? Object.keys(this.cvss)[0] : null;
      if (!value) {
        return 0;
      }
      const converted = parseFloat(value);
      if (!converted) {
        return 0;
      }
      return converted;
    },
    trend() {
      return this.metaData.trend;
    },
    vector() {
      return this.metaData.vector;
    },
    vectorString() {
      return this.vector?.vector?.vectorString;
    },
    epssScore() {
      return this.metaData.epss || 0;
    },
    epssFormattedScore() {
      let epss = this.epssScore || 0;
      epss = Math.round(((epss * 100) + Number.EPSILON) * 100) / 100;
      return epss === 0 ? '' : epss;
    },
    cveUrl() {
      return this.metaData?.cveUrl || this.issue.cveUrl;
    },
    kbUrl() {
      return this.metaData?.kbUrl || '';
    },
    urls() {
      const urlList = [];
      urlList.push(this.cveUrl);
      if (this.kbUrl !== '') {
        urlList.push(this.kbUrl);
      }
      return urlList;
    },
    aptGroups() {
      return this.issue.metaData?.enrichment?.apt_groups || [];
    },
    displayName() {
      return this.issue.metaData?.enrichment?.display_name || '';
    },
    hackersPerspective() {
      return this.issue.metaData?.enrichment?.hackers_perspective || '';
    },
    mitigations() {
      return this.issue.metaData?.enrichment?.mitigations || [];
    },
    references() {
      return this.issue.metaData?.enrichment?.references || [];
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.vulnerable-technology-issue-body {
  [class^="cve-enrichment-"]{
    padding-bottom: $space-2;
  }
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(420);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: rem(60);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-5;
    }
  }
  .cve-vector-block {
    display: block;
    max-width: rem(350);
    .cve-vector-data {
      padding-top: rem(2);
    }
  }
  .url-block, .cve-enrichment-apt-groups, .cve-enrichment-mitigations, .cve-enrichment-references{
    display: block;
    padding-top: $space-2;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .technical-block {
    padding-bottom: $space-2;
    display: block;
    .url-section {
      .bullet {
        padding-right: rem(5);
      }
      .text-container {
        padding-left: rem(5);
        padding-right: rem(60);
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
        padding-bottom: $space-4;
        max-width: rem(700);
      }
      display: flex;
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .technical-data {
      padding-top: rem(4);
    }
  }

  .cve-vector-details {
    margin-bottom: $space-5;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: rem(60);
      font-size: $font-size-sm;
      letter-spacing: 0;
      line-height: rem(18);
    }
  }

  .technology-name {
    text-transform: capitalize;
  }

  .cvss-score-section {
    margin-top: $space-2;
    align-items: center;
    display: flex;
    max-width: rem(70);
    margin-left: rem(3);
  }

  .epss-score {
    margin-top: $space-2;
    align-items: center;
    display: flex;
    max-width: rem(70);
    margin-left: rem(2);
  }

  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-minor-sub-title  {
    font-size: rem(8);
    color: var(--text-secondary);
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .cve-url {
    margin-top: rem(5);
  }
}
</style>
